import { useState, useEffect, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { History, CheckCircle2, XCircle, Clock, RefreshCw, ChevronRight } from 'lucide-react';
import { SyncHistoryEntry } from '../lib/offlineDb';
import { Link } from 'react-router-dom';

interface SyncHistoryPanelProps {
    trigger: React.ReactNode;
}

const MODULE_COLORS: Record<string, string> = {
    tasks: 'bg-blue-500/15 text-blue-300 border-blue-500/20',
    timeclock: 'bg-amber-500/15 text-amber-300 border-amber-500/20',
    compounding: 'bg-violet-500/15 text-violet-300 border-violet-500/20',
};

function timeAgo(dateStr: string) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

export default function SyncHistoryPanel({ trigger }: SyncHistoryPanelProps) {
    const [entries, setEntries] = useState<SyncHistoryEntry[]>([]);
    const [open, setOpen] = useState(false);

    const loadHistory = useCallback(async () => {
        const { getSyncHistory } = await import('../lib/offlineDb');
        const history = await getSyncHistory(20);
        setEntries(history);
    }, []);

    useEffect(() => {
        if (open) loadHistory();
    }, [open, loadHistory]);

    const syncedCount = entries.filter(e => e.status === 'synced').length;
    const failedCount = entries.filter(e => e.status === 'failed').length;

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>{trigger}</SheetTrigger>
            <SheetContent className="w-[380px] sm:w-[420px] p-0" data-testid="sync-history-panel">
                <SheetHeader className="px-5 pt-5 pb-3 border-b border-border/50">
                    <SheetTitle className="flex items-center gap-2 text-base">
                        <History className="w-4 h-4 text-primary" />
                        Recent Sync Activity
                    </SheetTitle>
                    <div className="flex items-center gap-2 pt-1">
                        {syncedCount > 0 && (
                            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/20 text-[10px]" data-testid="synced-count-badge">
                                <CheckCircle2 className="w-3 h-3 mr-1" />{syncedCount} synced
                            </Badge>
                        )}
                        {failedCount > 0 && (
                            <Badge className="bg-red-500/15 text-red-300 border-red-500/20 text-[10px]" data-testid="failed-count-badge">
                                <XCircle className="w-3 h-3 mr-1" />{failedCount} failed
                            </Badge>
                        )}
                        <Button variant="ghost" size="icon" className="h-6 w-6 ml-auto" onClick={loadHistory} data-testid="refresh-history-btn">
                            <RefreshCw className="w-3 h-3" />
                        </Button>
                    </div>
                </SheetHeader>

                <ScrollArea className="h-[calc(100vh-140px)]">
                    {entries.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground" data-testid="sync-history-empty">
                            <Clock className="w-8 h-8 mb-3 opacity-40" />
                            <p className="text-sm">No sync history yet</p>
                            <p className="text-xs mt-1 opacity-70">Actions queued offline will appear here after syncing</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-border/30">
                            {entries.map(entry => (
                                <div key={entry.id} className="px-5 py-3 hover:bg-muted/30 transition-colors" data-testid={`sync-entry-${entry.id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            {entry.status === 'synced' ? (
                                                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                                            ) : (
                                                <XCircle className="w-4 h-4 text-red-400 shrink-0" />
                                            )}
                                            <span className="text-sm font-medium truncate">{entry.description}</span>
                                        </div>
                                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{timeAgo(entry.syncedAt)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-1.5 ml-6">
                                        <Badge variant="outline" className={`text-[10px] h-4 px-1.5 border ${MODULE_COLORS[entry.module] || ''}`}>
                                            {entry.module}
                                        </Badge>
                                        <span className="text-[10px] text-muted-foreground">{entry.method}</span>
                                        {entry.user && <span className="text-[10px] text-muted-foreground">by {entry.user}</span>}
                                        {entry.retryCount > 0 && (
                                            <span className="text-[10px] text-amber-400">{entry.retryCount} retries</span>
                                        )}
                                    </div>
                                    {entry.errorMessage && (
                                        <p className="text-[10px] text-red-400/80 mt-1 ml-6 truncate">{entry.errorMessage}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>

                <div className="absolute bottom-0 left-0 right-0 border-t border-border/50 bg-background px-5 py-3">
                    <Link to="/admin" onClick={() => setOpen(false)}>
                        <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground hover:text-foreground" data-testid="view-full-history-btn">
                            View Full History in Admin
                            <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                    </Link>
                </div>
            </SheetContent>
        </Sheet>
    );
}
